import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "../Home/AxiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import {
  useTable,
  useGlobalFilter,
  useResizeColumns,
  useSortBy,
  usePagination,
} from "react-table";
import { FiEdit, FiTrash, FiArrowLeft } from "react-icons/fi";
import UserHeader from "./UserHeader";
import Footer from "../Home/Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "./employerPortal.css";

export default function JobApplications() {
  const [applications, setApplications] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [editingApp, setEditingApp] = useState(null);
  const [editedData, setEditedData] = useState({});
  const navigate = useNavigate();

  const { job_id } = useParams();

  useEffect(() => {
    if (job_id) fetchApplications();
  }, [job_id]);

  const fetchApplications = async () => {
    try {
      const res = await AxiosInstance.get(
        `companies/job-application/${job_id}/`
      );
      setApplications(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error loading job applications:", err);
      setApplications([]);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...editedData,
        trainee:
          typeof editedData.trainee === "object"
            ? editedData.trainee.trainee_id
            : editedData.trainee,
        job:
          typeof editedData.job === "object"
            ? editedData.job.job_id
            : editedData.job,
      };

      await AxiosInstance.put(
        `companies/job-applications/${editedData.application_id}`,
        payload
      );

      setEditingApp(null);
      fetchApplications();
    } catch (err) {
      console.error("Error saving job application:", err.response || err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await AxiosInstance.delete(`companies/job/application/${id}/`);
      fetchApplications();
    } catch (err) {
      console.error("Error deleting job application:", err);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Trainee Name",
        accessor: (row) =>
          row.trainee
            ? `${row.trainee.first_name} ${row.trainee.last_name}`
            : "-",
        resizable: true,
      },
      {
        Header: "Trainee Email",
        accessor: (row) => row.trainee?.email,
        resizable: true,
      },
      {
        Header: "Phone Number",
        accessor: (row) => row.trainee?.mobile_no || "-",
        resizable: true,
      },
      { Header: "Role", accessor: "job.role", resizable: true },
      {
        Header: "Resume",
        accessor: "resume_path",
        resizable: true,
        Cell: ({ value }) => {
          if (!value || value.includes("file://")) return "-";
          const resumeUrl = `${AxiosInstance.defaults.baseURL}media/${value}`;
          return (
            <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
              View Resume
            </a>
          );
        },
      },
      {
        Header: "Application Date",
        accessor: "application_date",
        resizable: true,
      },
      { Header: "Status", accessor: "status", resizable: true },
      { Header: "Offer Status", accessor: "offer_status", resizable: true },
      {
        Header: "Feedback",
        accessor: "feedback",
        Cell: ({ value }) => value || "-",
        resizable: true,
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setEditingApp(row.original);
                setEditedData({ ...row.original });
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row.original.application_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const data = useMemo(() => applications, [applications]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex },
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setGlobalFilter,
  } = useTable(
    { columns, data },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter(filterInput || undefined);
  }, [filterInput, setGlobalFilter]);

  return (
    <div className="CompanyPage als-employer-page">
      <UserHeader />
      <div className="company-container py-4">
        <div className="mb-3">
          <button
            className="navigate-button btn btn-outline-primary d-flex align-items-center"
            onClick={() => navigate("/user-profile")}
            style={{ gap: "6px" }}
          >
            <FiArrowLeft /> Back
          </button>
        </div>

        <h2 className="section-heading">Job Applications:</h2>
        <div className="card-custom">
          <div className="mb-3 d-flex justify-content-between">
            <input
              type="text"
              className="form-control w-50"
              placeholder="Search applications..."
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
            />
          </div>

          <div className="table-responsive">
            <table
              {...getTableProps()}
              className="table table-bordered table-striped"
            >
              <thead className="table-light">
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                    {hg.headers.map((col) => (
                      <th
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        key={col.id}
                      >
                        {col.render("Header")}
                        {col.isSorted ? (col.isSortedDesc ? " ▼" : " ▲") : ""}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={row.id}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} key={cell.column.id}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {page.length === 0 && (
                  <tr>
                    <td colSpan={columns.length}>No applications found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination d-flex align-items-center gap-2 mt-3">
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="btn btn-sm btn-outline-primary"
            >
              Previous
            </button>
            <span>
              Page <strong>{pageIndex + 1}</strong> of{" "}
              <strong>{pageOptions.length || 1}</strong>
            </span>
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="btn btn-sm btn-outline-primary"
            >
              Next
            </button>
          </div>

          {editingApp && (
            <div
              className="modal d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Application</h5>
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={editedData.status || ""}
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              status: e.target.value,
                            })
                          }
                        >
                          <option value="Applied">Applied</option>
                          <option value="Shortlisted">Shortlisted</option>
                          <option value="Interview Scheduled">
                            Interview Scheduled
                          </option>
                          <option value="Selected">Selected</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Withdrawn">Withdrawn</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Offer Status</label>
                        <select
                          className="form-select"
                          value={editedData.offer_status || ""}
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              offer_status: e.target.value,
                            })
                          }
                        >
                          <option value="Pending">Pending</option>
                          <option value="Accepted">Accepted</option>
                          <option value="Declined">Declined</option>
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Feedback</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={editedData.feedback || ""}
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              feedback: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingApp(null)}
                    >
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSave}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
